import { useState, useCallback, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import AgentTerminal from './agent-terminal/AgentTerminal'
import GeometryPreview from '../shared/GeometryPreview'
import type { JobWizardConfig, WorkOffset } from '../../types'
import type { MachineProfile, Tool } from '../../types'

interface MissionControlProps {
  onAddJob: (config: JobWizardConfig) => void
  profiles: MachineProfile[]
  onManageProfiles: () => void
  inventoryTools?: Tool[]
}

export default function MissionControl({ onAddJob, profiles, onManageProfiles, inventoryTools = [] }: MissionControlProps) {
  const [geometry, setGeometry] = useState<Record<string, unknown> | null>(null)
  const [stepFile, setStepFile] = useState<File | null>(null)
  const [filesSubmitted, setFilesSubmitted] = useState(false)
  const [workOffsets, setWorkOffsets] = useState<WorkOffset[]>([])
  const [meshBounds, setMeshBounds] = useState<{ min: [number, number, number]; max: [number, number, number] } | null>(null)

  const handleWorkOffsetChange = useCallback((offset: { register: string; x: number; y: number; z: number }) => {
    setWorkOffsets(prev => {
      // Replace if same register exists, otherwise append
      const existing = prev.findIndex(o => o.register === offset.register)
      if (existing >= 0) {
        const updated = [...prev]
        updated[existing] = offset as WorkOffset
        return updated
      }
      return [...prev, offset as WorkOffset]
    })
  }, [])

  const handleSubmit = (config: JobWizardConfig) => {
    onAddJob(config)
    // Navigation handled by App.tsx handleAddJob which includes the job ID
  }

  const previewRef = useRef<HTMLDivElement>(null)
  const hasScrolled = useRef(false)

  const handleGeometryChange = useCallback((geo: Record<string, unknown> | null) => {
    setGeometry(geo)
  }, [])

  const handleStepFileChange = useCallback((file: File | null) => {
    setStepFile(file)
  }, [])

  const hasGeometry = geometry !== null

  // Auto-scroll so the full preview container fits in the viewport when geometry first appears
  useEffect(() => {
    if (hasGeometry && !hasScrolled.current && previewRef.current) {
      hasScrolled.current = true
      // Small delay to let the layout animate into split view
      setTimeout(() => {
        previewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 400)
    }
  }, [hasGeometry])

  return (
    <div
      className="relative flex flex-col items-center"
      style={{ minHeight: '100vh', paddingBottom: '4rem' }}
    >
      {/* Ambient glow effects */}
      <div
        className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(6,182,212,0.06) 0%, transparent 70%)',
        }}
      />

      {/* Terminal header */}
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className={`relative z-10 w-full px-6 pt-10 pb-6 ${hasGeometry ? '' : 'max-w-2xl'}`}
      >
        <h1 className="font-mono text-xs text-cyan-500/60 tracking-[0.3em] uppercase mb-1">
          GCODR TERMINAL
        </h1>
        {!filesSubmitted && (
          <p className="font-mono text-sm text-slate-500">
            Drop your engineering files. The agent will analyze and guide you.
          </p>
        )}
      </motion.div>

      {/* Terminal body — split layout when geometry present */}
      <div
        ref={previewRef}
        className={`relative z-10 w-full px-6 transition-all duration-500 ease-in-out ${
          hasGeometry ? 'flex gap-6' : 'max-w-2xl'
        }`}
      >
        {/* Left: Terminal */}
        <div
          className="transition-all duration-500 ease-in-out"
          style={{
            width: hasGeometry ? '40%' : '100%',
            minWidth: hasGeometry ? '480px' : undefined,
            maxWidth: hasGeometry ? '600px' : undefined,
          }}
        >
          <AgentTerminal
            profiles={profiles}
            inventoryTools={inventoryTools}
            onSubmit={handleSubmit}
            onGeometryChange={handleGeometryChange}
            onStepFileChange={handleStepFileChange}
            onManageProfiles={onManageProfiles}
            onFilesSubmitted={() => setFilesSubmitted(true)}
            workOffsets={workOffsets}
            meshBounds={meshBounds}
          />
        </div>

        {/* Right: 3D preview panel (sticky, stays visible while scrolling) */}
        <AnimatePresence>
          {hasGeometry && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="flex-1 min-w-0 hidden lg:block"
            >
              <div
                className="sticky top-4 rounded-xl overflow-hidden border border-cyan-500/15 bg-[#030712]"
                style={{ height: 'calc(100vh - 6rem)' }}
              >
                <div className="px-3 py-2 border-b border-cyan-500/10 flex items-center">
                  <span className="font-mono text-[10px] tracking-widest uppercase text-slate-500">
                    Part Preview
                  </span>
                </div>
                <GeometryPreview
                  geometry={geometry}
                  height="calc(100% - 33px)"
                  stepFile={stepFile}
                  workOffsets={workOffsets}
                  onWorkOffsetChange={handleWorkOffsetChange}
                  onMeshBoundsChange={setMeshBounds}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
